import moment, { Moment } from 'moment'

import {
	DAYS_IN_WEEK,
	FIRST_DAY_OF_WEEK,
	PERIOD_DURATIONS,
	WEEKS_TO_DISPLAY,
} from '../constants/constants'
import { DateRange } from '../types'

/**
 * Получает период "Сегодня" (от начала дня до текущего момента)
 */
export const getTodayRange = (): DateRange => {
	const nowDate = moment()
	const startOfDay = nowDate.clone().subtract(1, 'day').endOf('day')
	const endOfDay = nowDate.endOf('day')

	return {
		startDate: startOfDay.valueOf() + 1,
		endDate: endOfDay.valueOf(),
	}
}

/**
 * Получает период "Вчера" (весь вчерашний день)
 */
export const getYesterdayRange = (): DateRange => {
	const currentEndDate = moment().startOf('day')
	const endDate = moment().startOf('day').subtract(1, 'minutes').valueOf()
	const startDate = moment(currentEndDate).subtract(1, 'days').valueOf()

	return { startDate, endDate }
}

/**
 * Получает период "Неделя" (последние 7 дней включая сегодня)
 */
export const getWeekRange = (): DateRange => {
	const currentEndDate = moment().valueOf()
	const endDateTime = moment().startOf('day').valueOf()
	// Вычитаем 6 дней, чтобы получить ровно 7 дней (6 прошлых + сегодня)
	const startDate = moment(endDateTime)
		.subtract(PERIOD_DURATIONS.WEEK - 1, 'days')
		.valueOf()

	return { startDate, endDate: currentEndDate }
}

/**
 * Получает период "Неделя" для будущих дат (следующие 7 дней включая сегодня)
 */
export const getFutureWeekRange = (): DateRange => {
	const startDate = moment().startOf('day').valueOf()
	// Добавляем 6 дней, чтобы получить ровно 7 дней (сегодня + 6 будущих)
	const endDate = moment(startDate)
		.add(PERIOD_DURATIONS.WEEK - 1, 'days')
		.endOf('day')
		.valueOf()

	return { startDate, endDate }
}

/**
 * Получает период "Месяц" (последние 30 дней)
 */
export const getMonthRange = (): DateRange => {
	const currentEndDate = moment().valueOf()
	const endDateTime = moment().startOf('day').valueOf()
	const startDate = moment(endDateTime).subtract(1, 'months').valueOf()

	return { startDate, endDate: currentEndDate }
}

/**
 * Получает период "Месяц" для будущих дат (следующие 30 дней)
 */
export const getFutureMonthRange = (): DateRange => {
	const startDate = moment().startOf('day').valueOf()
	const endDate = moment(startDate).add(1, 'months').valueOf()

	return { startDate, endDate }
}

/**
 * Получает период "Год" (последний год)
 * Использует moment.js для корректного расчета с учетом високосных годов
 */
export const getYearRange = (): DateRange => {
	const currentEndDate = moment().valueOf()
	const endDateTime = moment().startOf('day').valueOf()
	const startDate = moment(endDateTime).subtract(1, 'year').valueOf()

	return { startDate, endDate: currentEndDate }
}

/**
 * Получает период "Год" для будущих дат (следующий год)
 */
export const getFutureYearRange = (): DateRange => {
	const startDate = moment().startOf('day').valueOf()
	const endDate = moment(startDate).add(1, 'year').valueOf()

	return { startDate, endDate }
}

/**
 * Генерирует массив дат для отображения в календаре
 * Включает дни предыдущего и следующего месяцев для заполнения сетки
 */
export const generateCalendarDays = (currentDate: Moment): Moment[] => {
	const days: Moment[] = []

	// Первый день текущего месяца
	const firstDayOfMonth = currentDate.clone().startOf('month')

	// Определяем день недели первого дня месяца (0 - воскресенье, 1 - понедельник)
	let dayOfWeek = firstDayOfMonth.day()

	// Корректируем для недели, начинающейся с понедельника
	if (FIRST_DAY_OF_WEEK === 1) {
		dayOfWeek = dayOfWeek === 0 ? 6 : dayOfWeek - 1
	}

	// Находим первый день для отображения (может быть из предыдущего месяца)
	const startDate = firstDayOfMonth.clone().subtract(dayOfWeek, 'days')

	// Генерируем WEEKS_TO_DISPLAY недель (42 дня = 6 недель)
	const totalDays = WEEKS_TO_DISPLAY * DAYS_IN_WEEK

	for (let i = 0; i < totalDays; i++) {
		days.push(startDate.clone().add(i, 'days'))
	}

	const weeks = groupDaysByWeeks(days, currentDate)

	return weeks.flat()
}

/**
 * Группирует дни по неделям для отображения в календаре
 */
export const groupDaysByWeeks = (
	days: Moment[],
	currentDate?: Moment,
): Moment[][] => {
	const weeks: Moment[][] = []

	for (let i = 0; i < days.length; i += DAYS_IN_WEEK) {
		weeks.push(days.slice(i, i + DAYS_IN_WEEK))
	}

	if (currentDate && weeks.length > 0) {
		const lastWeek = weeks[weeks.length - 1]
		const hasCurrentMonthDay = lastWeek.some(day =>
			day.isSame(currentDate, 'month'),
		)

		if (!hasCurrentMonthDay) {
			weeks.pop()
		}
	}

	return weeks
}

/**
 * Генерирует массив годов для YearPicker
 */
export const generateYearRange = (
	selectedYear: number,
	yearsBefore = 100,
	yearsAfter = 10,
): number[] => {
	const years: number[] = []
	const startYear = selectedYear - yearsBefore
	const endYear = selectedYear + yearsAfter

	for (let year = startYear; year <= endYear; year++) {
		years.push(year)
	}

	return years
}

/**
 * Применяет сдвиг дней к периоду (для dayBefore prop)
 */
export const applyDayOffset = (
	range: DateRange,
	dayOffset?: number,
): DateRange => {
	if (!dayOffset) return range

	const oneDayInMilliseconds = 86400000
	const offset = dayOffset * oneDayInMilliseconds

	return {
		startDate: range.startDate - offset,
		endDate: range.endDate - offset,
	}
}

/**
 * Вычисляет количество дней между двумя датами
 */
export const getDaysBetweenDates = (
	startDate: Moment,
	endDate: Moment,
): number => {
	const start = startDate.clone().startOf('day')
	const end = endDate.clone().endOf('day')

	const daysDifference = end.diff(start, 'days')

	return Math.max(0, daysDifference)
}

/**
 * Получает первый день недели для конкретной даты
 */
export const getStartOfWeek = (date: Moment): Moment => {
	return date
		.clone()
		.startOf('week')
		.add(FIRST_DAY_OF_WEEK === 1 ? 1 : 0, 'days')
}

/**
 * Получает последний день недели для конкретной даты
 */
export const getEndOfWeek = (date: Moment): Moment => {
	return date
		.clone()
		.endOf('week')
		.add(FIRST_DAY_OF_WEEK === 1 ? 1 : 0, 'days')
}
